import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CirclePlus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GoalCard, { Goal, cardVariants } from './GoalCard';
import { Trash2 } from 'lucide-react';
import { useTopGoalEncouragement } from '@/hooks/goal-planner/use-encouragement';

interface CompletedGoalsSectionProps {
  goals: Goal[];
  activeGoals?: Goal[]; // Add activeGoals prop for encouragement
  showCompleted: boolean;
  onToggle: () => void;
  onClearCompleted: (e: React.MouseEvent) => void;
  onCardClick: (goalId: string) => void;
}

const CompletedGoalsSection: React.FC<CompletedGoalsSectionProps> = ({ 
  goals, 
  activeGoals = [],
  showCompleted, 
  onToggle, 
  onClearCompleted, 
  onCardClick 
}) => {
  // Get encouragement message from the highest progress active goal
  const encouragement = useTopGoalEncouragement(activeGoals);

  return (
    <div className="relative p-3 md:p-6 rounded-2xl overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10" />
      
      {/* Top border highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent dark:via-amber-500/20" />
      
      {/* Glass effect */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10" />
      <div 
        className="flex items-center justify-between mb-3 md:mb-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <CirclePlus className="text-green-500 dark:text-green-400" size={18} />
          <h3 className="font-medium text-base">Completed Goals</h3>
          {goals.length > 0 && (
            <span className="ml-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs h-6 w-6 flex items-center justify-center rounded-full p-0">
              {goals.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {goals.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="group h-8 px-3 text-muted-foreground bg-transparent hover:bg-green-500 hover:text-white dark:hover:bg-green-600 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                onClearCompleted(e);
              }}
            >
              <Trash2 size={16} className="mr-1 group-hover:animate-pulse" />
              <span className="text-xs font-medium">Clear all</span>
            </Button>
          )}
          <button className="text-muted-foreground hover:text-foreground transition-colors">
            {showCompleted ? (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="m18 15-6-6-6 6"/>
              </svg>
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showCompleted && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1.0]
            }}
            className="relative overflow-hidden"
          >
            <div className="pt-2 overflow-visible">
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {goals.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="relative"
                    >
                      {/* Celebration Effects - Only show if encouragement has sparkles */}
                      <AnimatePresence>
                        {encouragement.showSparkles && (
                          <>
                            {[...Array(3)].map((_, i) => (
                              <motion.div
                                key={`sparkle-${i}`}
                                className="absolute pointer-events-none"
                                style={{
                                  left: `${15 + i * 25}%`,
                                  top: `${20 + (i % 2) * 20}%`,
                                }}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ 
                                  opacity: [0, 1, 0],
                                  scale: [0, 1, 0],
                                  y: [0, -20]
                                }}
                                exit={{ opacity: 0 }}
                                transition={{
                                  duration: 1.2,
                                  delay: i * 0.4,
                                  ease: "easeOut"
                                }}
                              >
                                <Sparkles className="h-2 w-2 text-yellow-400" />
                              </motion.div>
                            ))}
                          </>
                        )}
                      </AnimatePresence>

                      {/* Encouragement Message */}
                      <div className={`p-4 rounded-xl border-2 ${encouragement.cardBg} ${encouragement.cardBorder} shadow-sm relative z-10`}>
                        <h4 className={`font-semibold text-center ${encouragement.cardColor}`}>
                          {encouragement.message}
                        </h4>
                      </div>
                    </motion.div>
                  ) : (
                    goals.map((goal) => (
                      <motion.div
                        key={goal.id}
                        layoutId={`completed-goal-${goal.id}`}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={cardVariants}
                        whileHover={{ scale: 1.02 }}
                        transition={{ layout: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] } }}
                      >
                        <GoalCard 
                          goal={goal} 
                          onClick={onCardClick}
                          isDragging={false}
                        />
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompletedGoalsSection;
